import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Select from 'react-select';

import Loading from '../../../shared/loading';
import DropdownIndicator from '../dropdown-styles';
import sendContact from '../../../services/partnerships/contact-service';
import EnquiryContext from './context';
import Validator from '../../../shared/validator';
import { EMAIL_REGEX } from '../../../shared/consts';

const selectorStyles = {
  container: provided => ({
    ...provided,
    fontSize: 18,
    fontWeight: 400,
    textTransform: 'none',
  }),
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? '#6f8aa1' : '#e4eaee',
    '&:hover': {
      borderColor: '#6f8aa1',
    },
    borderRadius: 4,
    outline: 'none',
    boxShadow: 'none',
    height: 50,
  }),
  indicatorsContainer: provided => ({
    ...provided,
    height: 50,
    paddingRight: 8,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  option: (provided, state) => ({
    ...provided,
    // eslint-disable-next-line no-nested-ternary
    backgroundColor: state.isSelected ? '#19202d' : (state.isFocused ? '#f3f5fa' : '#fff'),
    fontSize: 18,
    fontWeight: 400,
    textTransform: 'none',
  }),
  placeholder: provided => ({
    ...provided,
    color: '#8ea3b5',
  }),
  valueContainer: provided => ({
    ...provided,
    marginTop: -5,
    paddingLeft: 16,
  }),
  singleValue: provided => ({
    ...provided,
    color: '#19202d',
  }),
};

const selectorProps = {
  styles: selectorStyles,
  classNamesPrefix: 'sel',
  components: { DropdownIndicator },
};

const Container = styled.form`
  display: flex;
  flex-direction: column;

  .loading-panel {
    margin-left: auto;
    width: 136px;
  }
`;

const Details = styled.div`
  display: flex;
  flex-direction: row;

  ${props => props.theme.isMobileOrTablet && `
    flex-direction: column;
  `};
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 12px;
  width: 50%;

  ${props => props.theme.isMobileOrTablet && `
    width: 100%;
    padding: 0;
  `};
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  padding-left: 12px;

  ${props => props.theme.isMobileOrTablet && `
    width: 100%;
    padding: 0;
  `};
`;

const Label = styled.label`
  color: ${props => props.theme.bodyColor};
  font-size: 16px;
  font-weight: 800;
  margin: 24px 0 10px 0;
`;

const InputText = styled.input`
  height: 50px;
  border-radius: 4px;
  border: 1px solid #E9EEF3;
  padding-left: 16px;
  font-size: 19px;
`;

const Checkbox = styled.label`
  color: ${props => props.theme.bodyColor};
  font-size: 16px;
  margin-bottom: 8px;
`;

const Textarea = styled.textarea`
  height: 220px;
  border-radius: 4px;
  border: 1px solid #E9EEF3;
  padding: 16px;
  font-size: 19px;
  resize: vertical;
`;

const Button = styled.button`
  border-radius: 2px;
  border-width: 0px;
  height: 36px;
  padding: 5px 0;
  background-color: #191F2D;
  color: white;
  font-size: 14px;
  font-weight: ${props => props.theme.fontWeightHeavy};
  margin-top: 24px;
  margin-left: auto;
  text-transform: uppercase;
  width: 136px;
  outline: none;
`;

const HelpBlock = styled.span`
  color: ${props => props.theme.errorColor};
  font-size: 16px;
  font-weight: ${props => props.theme.fontWeightMedium};
  line-height: 120%;
  text-transform: none;
`;

const Footer = styled.footer`
  margin-top: 48px;

  & > p {
    color: ${props => props.theme.bodyColor};
    font-size: 14px;
    font-weight: normal;
    line-height: 130%;
  }
`;

export default function Form({ referable, investorTypes, messageToLabel }) {
  const {
    id: referableId,
    type: referableType,
    activePartnership: { id: activePartnershipId },
  } = referable;

  const { currentUser } = window.Livewire;

  const loadingRef = React.useRef();

  const { dispatch } = React.useContext(EnquiryContext);

  const mappedInvestorTypes = [
    { label: null, value: null },
    ...investorTypes.map(type => ({ label: type.name, value: type.id })),
  ];

  const [formData, setFormData] = React.useState({
    name: _.get(currentUser, 'profile.name'),
    email: _.get(currentUser, 'email'),
    investorType: _.find(mappedInvestorTypes, { value: _.get(currentUser, 'profile.investorType') }),
    enquiryAboutOne: null,
    enquiryAboutTwo: null,
    enquiryAboutThree: null,
  });

  const [validations, setValidations] = React.useState({
    nameRequiredValid: true,
    emailRequiredValid: true,
    emailFormatValid: true,
  });

  const validator = new Validator({
    nameRequired: state => !!state.name.trim(),
    emailRequired: state => !!state.email.trim(),
    emailFormat: state => !state.email.trim() || EMAIL_REGEX.test(state.email),
  });

  const handleChange = e => {
    const { target } = e;
    const newData = { ...formData };
    newData[target.name] = target.value;
    setFormData(newData);
  };

  const handleInvestorTypeChange = type => {
    const newData = { ...formData };
    newData.investorType = type;
    setFormData(newData);
  };

  const handleEnquiryAboutChange = e => {
    const { target } = e;
    const newData = { ...formData };
    newData[target.name] = target.checked ? target.value : null;
    setFormData(newData);
  };

  const createContact = e => {
    e && e.preventDefault();

    validator.validateForm(formData);
    setValidations(validator.getValidations());

    if (!validator.formValid) {
      return;
    }

    const {
      enquiryAboutOne,
      enquiryAboutTwo,
      enquiryAboutThree,
      investorType,
    } = formData;

    const investorTypeName = investorType ? investorType.label : null;

    loadingRef.current.start();

    sendContact(activePartnershipId, {
      referable_id: referableId,
      referable_type: referableType,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      investor_type: investorTypeName,
      enquiring_about: _.compact([enquiryAboutOne, enquiryAboutTwo, enquiryAboutThree]),
      message_to_contributor: formData.message,
      page: window.location.pathname,
    }).then(() => {
      loadingRef.current.stop();
      dispatch({ type: 'contactSent' });
    }).catch(() => (loadingRef.current.stop()));
  };

  const ENQUIRY_ABOUT_OPTIONS = [
    { name: 'enquiryAboutOne', value: 'Book a meeting/call' },
    { name: 'enquiryAboutTwo', value: 'Request information' },
    { name: 'enquiryAboutThree', value: 'Attend your next event' },
  ];

  return (
    <Container onSubmit={createContact}>
      <Details>
        <Left>
          <Label>
            NAME
            <InputText type="text" name="name" value={formData.name} onChange={handleChange} />
            {validations.nameRequiredValid || <HelpBlock>Name is required</HelpBlock>}
          </Label>
          <Label>
            PHONE
            <InputText type="text" name="phone" value={formData.phone} onChange={handleChange} />
          </Label>
        </Left>
        <Right>
          <Label>
            EMAIL
            <InputText type="text" name="email" value={formData.email} onChange={handleChange} />
            {validations.emailRequiredValid || <HelpBlock>Email is required</HelpBlock>}
            {validations.emailFormatValid || <HelpBlock>Email is not in correct format</HelpBlock>}
          </Label>
          <Label>
            INVESTOR TYPE
            <Select
              {...selectorProps}
              className="investor-type"
              value={formData.investorType}
              options={mappedInvestorTypes}
              placeholder="Investor type"
              onChange={handleInvestorTypeChange}
            />
          </Label>
        </Right>
      </Details>
      <Label>I WOULD LIKE TO</Label>
      {ENQUIRY_ABOUT_OPTIONS.map(option => (
        <Checkbox key={option.name}>
          <input type="checkbox" name={option.name} value={option.value} checked={!!formData[option.name]} onChange={handleEnquiryAboutChange} />
          {option.value}
        </Checkbox>
      ))}
      <Label>
        {messageToLabel}
        <Textarea name="message" value={formData.message} onChange={handleChange} />
      </Label>
      <Loading ref={loadingRef} variant="button">
        <Button type="submit">Send enquiry</Button>
      </Loading>
      <Footer>
        <p>
          Only to be used for sending genuine email enquiries to the Contributor. Livewire
          Markets Pty Ltd reserves its right to take any legal or other appropriate action in
          relation to misuse of this service.
        </p>
        <p>
          <strong>Personal Information Collection Statement</strong>
          <br />
          Your personal information will be passed to the Contributor and/or its authorised
          service provider to assist the Contributor to contact you about your investment
          enquiry. They are required not to use your information for any other purpose. Our
          &nbsp;
          <a href="/company/privacy" target="_new">privacy policy</a>
          &nbsp;
          explains how we store personal information and how you may access, correct or complain
          about the handling of personal information.
        </p>
      </Footer>
    </Container>
  );
}

Form.propTypes = {
  referable: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    activePartnership: PropTypes.shape({
      id: PropTypes.number,
    }),
  }).isRequired,
  investorTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ).isRequired,
  messageToLabel: PropTypes.string.isRequired,
};
